import { type ClassValue, clsx } from 'clsx';
import type { Thing, WithContext } from 'schema-dts';
import { twMerge } from 'tailwind-merge';
import { CURRENCY_CODE, LANGUAGE_CODE } from './constants';
import type { Money, ShopifyErrorLike } from './types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const ensureStartsWith = (stringToCheck: string, startsWith: string) =>
  stringToCheck.startsWith(startsWith)
    ? stringToCheck
    : `${startsWith}${stringToCheck}`;

function findError<T extends object>(error: T): boolean {
  if (Object.prototype.toString.call(error) === '[object Error]') {
    return true;
  }

  const prototype = Object.getPrototypeOf(error) as T | null;

  return prototype === null ? false : findError(prototype);
}

export const isObject = (
  object: unknown
): object is Record<string, unknown> => {
  return (
    typeof object === 'object' && object !== null && !Array.isArray(object)
  );
};

export const isShopifyError = (error: unknown): error is ShopifyErrorLike => {
  if (!isObject(error)) {
    return false;
  }

  if (error instanceof Error) {
    return true;
  }

  if (error.message) {
    return true;
  }

  return findError(error);
};

export function removeProductGid(id: string) {
  return id.replace('gid://shopify/Product/', '');
}

export function removeGid(id: string) {
  if (!id) return '';
  const withoutGid = id.split('/').pop();
  if (!withoutGid) return '';
  return withoutGid;
}

export function formatPrice(price: number) {
  const amountInt = Math.ceil(price);

  return new Intl.NumberFormat(LANGUAGE_CODE, {
    style: 'currency',
    currency: CURRENCY_CODE,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amountInt);
}

export function formatMoney(money: Money) {
  return formatPrice(Number(money.amount));
}

export function formatDate(dateString: string, type?: 'short' | 'full') {
  const date = new Date(dateString);
  if (type === 'full') {
    return date.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }
  return date.toLocaleString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}

export const radixOverlayStyles =
  'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 backdrop-blur-xs bg-white/50 data-[state=closed]:animate-out data-[state=open]:animate-in';

export const baseUiOverlayStyles =
  'fixed z-50 inset-0 bg-white/50 backdrop-blur-xs transition-all duration-150 data-[ending-style]:opacity-0 data-[starting-style]:opacity-20';

export const sanitizeJsonLd = <T extends WithContext<Thing>>(json: T) => {
  try {
    return JSON.stringify(JSON.parse(JSON.stringify(json)));
  } catch {
    return '{}';
  }
};

// TODO fix
export function isNotInProduction() {
  // CI is a special env var that Vercel sets during builds
  // It's NOT included in the env vars pulled by 'vercel env pull'
  if (process.env.CI === '1' || process.env.CI === 'true') {
    // We're in Vercel/CI, check if it's production
    return process.env.VERCEL_ENV !== 'production';
  }

  // // If CI is not set, we're in local development
  // // Skip pre-rendering even if using production env vars
  return true;
}
